import React from 'react';
import { Plane, Menu, X, Search, ChevronDown, ArrowLeft } from 'lucide-react';
import { MobileMenu } from './MobileMenu';
import { InternalLink } from './InternalLink';

interface HeaderProps {
  onNavigate?: (path: string) => void;
}

export function Header({ onNavigate }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isToolsOpen, setIsToolsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchButtonRef = React.useRef<HTMLButtonElement>(null);
  const toolsButtonRef = React.useRef<HTMLButtonElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchContainerRef = React.useRef<HTMLDivElement>(null);

  // Close tools dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsToolsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when search opens, handle escape key, and click outside
  React.useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && searchOpen) {
        closeSearch();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (searchOpen && searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchOpen]);

  const navigate = (path: string) => {
    onNavigate && onNavigate(path);
    setMobileMenuOpen(false);
    setIsToolsOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/airports?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    window.requestAnimationFrame(() => searchButtonRef.current?.focus());
  };

  const tools = [
    { name: 'Flight Time Calculator', path: '/flight-calculator' },
    { name: 'Currency Converter', path: '/currency-converter' },
    { name: 'Travel Checklist', path: '/checklist' },
    { name: 'Airport Quiz', path: '/quiz' },
    { name: 'Runway Boss Game', path: '/game' },
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsToolsOpen(!isToolsOpen);
    } else if (e.key === 'Escape') {
      setIsToolsOpen(false);
      toolsButtonRef.current?.focus();
    }
  };

  const handleToolClick = (path: string) => {
    navigate(path);
    setIsToolsOpen(false);
  };

  const handleToolKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToolClick(tools[index].path);
    } else if (e.key === 'Escape') {
      setIsToolsOpen(false);
      toolsButtonRef.current?.focus();
    }
  };

  return (
    <header className="bg-green-700 text-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Search Mode - Takes over header */}
          {searchOpen ? (
            <div ref={searchContainerRef} className="flex items-center gap-3 w-full animate-fade-in">
              {/* Back/Close Button */}
              <button
                onClick={closeSearch}
                className="p-2 hover:bg-green-600 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                aria-label="Close search"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {/* Search Form - Full Width */}
              <form onSubmit={handleSearch} className="flex-1 relative" style={{ minWidth: 0 }}>
                <input
                  ref={searchInputRef}
                  type="search"
                  aria-label="Search airports, cities, or IATA codes"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search airports, cities, or IATA codes..."
                  className="w-full h-11 pr-10 bg-white text-gray-900 text-lg font-medium rounded-lg border border-gray-200 focus:outline-none focus:border-green-500 focus:ring-4 focus:ring-green-500/20 placeholder:text-gray-400 transition-all shadow-sm"
                  style={{ paddingLeft: '60px' }}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <div className="absolute left-0 top-0 bottom-0 w-16 flex items-center justify-center pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </form>

              {/* Search Submit Button */}
              <button
                type="submit"
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
                className="px-4 py-2 bg-white text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 hidden sm:block"
              >
                Search
              </button>
            </div>
          ) : (
            <>
              {/* Logo */}
              <InternalLink
                to="/"
                onNavigate={onNavigate}
                className="flex items-center gap-2 hover:opacity-90 transition-opacity cursor-pointer"
              >
                <Plane className="w-8 h-8" />
                <span className="text-2xl font-bold">airports.ng</span>
              </InternalLink>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-6">
                <InternalLink
                  to="/airports"
                  onNavigate={onNavigate}
                  className="hover:text-green-200 transition-colors cursor-pointer"
                >
                  Airports
                </InternalLink>

                <InternalLink
                  to="/passenger-rights"
                  onNavigate={onNavigate}
                  className="hover:text-green-200 transition-colors cursor-pointer"
                >
                  Passenger Rights
                </InternalLink>

                <InternalLink
                  to="/blog"
                  onNavigate={onNavigate}
                  className="hover:text-green-200 transition-colors cursor-pointer"
                >
                  Travel Blog
                </InternalLink>

                {/* Tools Dropdown */}
                <div className="relative flex items-center gap-1" ref={dropdownRef}>
                  <InternalLink
                    to="/tools"
                    onNavigate={onNavigate}
                    className="hover:text-green-200 transition-colors cursor-pointer"
                  >
                    Travel Tools
                  </InternalLink>
                  <button
                    ref={toolsButtonRef}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsToolsOpen(!isToolsOpen);
                    }}
                    onKeyDown={handleKeyDown}
                    className="hover:text-green-200 transition-colors cursor-pointer p-1"
                    aria-expanded={isToolsOpen}
                    aria-haspopup="true"
                    aria-label="Toggle tools menu"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${isToolsOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isToolsOpen && (
                    <div className="absolute top-full mt-2 right-0 bg-white text-gray-900 rounded-lg shadow-xl py-2 min-w-[220px] border-2 border-green-100 animate-slide-down">
                      {tools.map((tool, index) => (
                        <InternalLink
                          key={tool.path}
                          to={tool.path}
                          onNavigate={navigate}
                          onKeyDown={(e) => handleToolKeyDown(e, index)}
                          className="block w-full text-left px-4 py-2 hover:bg-green-50 transition-colors cursor-pointer"
                          role="menuitem"
                        >
                          {tool.name}
                        </InternalLink>
                      ))}
                    </div>
                  )}
                </div>
              </nav>

              {/* Search & Mobile Menu Button */}
              <div className="flex items-center gap-4">
                <button
                  ref={searchButtonRef}
                  onClick={() => { setMobileMenuOpen(false); setSearchOpen(true); }}
                  className="p-2 hover:bg-green-600 rounded-lg transition-colors cursor-pointer"
                  aria-label="Open search"
                >
                  <Search className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 hover:bg-green-600 rounded-lg transition-colors cursor-pointer"
                  aria-label="Toggle menu"
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-navigation"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Mobile Menu Component (Issue 7.2) */}
        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          onNavigate={navigate}
        />
      </div>
    </header>
  );
}
